import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { logError } from 'components/LogError';
import { FinalizePaymentSessionMutation } from '__generated__/graphql';
import { FINALIZE_PAYMENT_SESSION } from '../graphql/finalizePaymentSession';
import { GET_CHECKOUT_DOT_COM_CARDS } from '../graphql/getCheckoutDotComCards';

interface ReturnProps {
  finalizeSession: (paymentId: string) => Promise<FinalizePaymentSessionMutation['finalizePaymentSession'] | null>;
  loading: boolean;
}

/**
 * checkout.com FULL FLOW migration. After the Flow SDK completes a charge synchronously, its
 * `onPaymentCompleted` callback gives us the `pay_...` id immediately. We hand it to the backend here so
 * it can pull the payment (`GET /payments/{id}`) and run the card-storage / MIT-consent logic right
 * away, instead of waiting on the asynchronous checkout.com webhook (which is slow and never reaches
 * localhost). See `src/views/Accounts/graphql/finalizePaymentSession.ts` for the full rationale.
 *
 * Best-effort: failures are logged but swallowed so the success navigation still proceeds — the webhook
 * remains the backstop for card persistence.
 */
export function useFinalizeCheckoutDotComPaymentSession(): ReturnProps {
  const [postFinalizePaymentSession, { loading }] = useMutation<FinalizePaymentSessionMutation>(
    FINALIZE_PAYMENT_SESSION,
    {
      // Refresh the saved-card list once the backend has stored the card, so it appears without a manual
      // refresh. Only refetches if the query is currently active, so it's harmless on other pages.
      refetchQueries: [{ query: GET_CHECKOUT_DOT_COM_CARDS }],
      onError: (error) => logError(error, { tags: { userFlow: 'payment' } }),
    },
  );

  const finalizeSession = useCallback(
    async (paymentId: string) => {
      try {
        const { data } = await postFinalizePaymentSession({
          variables: { finalizePaymentSession: { paymentId } },
        });
        const result = data?.finalizePaymentSession;

        if (result?.errorMessage) {
          logError(new Error(result.errorMessage), { tags: { userFlow: 'payment' } });
        }

        return result ?? null;
      } catch (error) {
        logError(error as Error, { tags: { userFlow: 'payment' } });
        return null;
      }
    },
    [postFinalizePaymentSession],
  );

  return { finalizeSession, loading };
}
